// AI Conversation — listing + detail for ElevenLabs / WhatsApp AI agent
// conversations. Table styled after the ElevenLabs dashboard: agent badge,
// title, date, duration, message count, status + evaluation badges.
// Detail drawer: overview grid, chat transcript, audio playback (voice calls),
// and CRM linking (open matched lead, or Create Lead).

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  AudioLines, Search, Bot, MessageCircle, Phone, Clock, X, Loader2,
  ChevronLeft, ChevronRight, UserPlus, ExternalLink, Play, RefreshCw,
} from "lucide-react";
import { useBusiness } from "@/contexts/BusinessContext";
import { DATE_PRESETS, computeDatePreset } from "@/utils/datePresets";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  done:        { cls: "bg-emerald-100 text-emerald-700", label: "Successful" },
  completed:   { cls: "bg-emerald-100 text-emerald-700", label: "Successful" },
  in_progress: { cls: "bg-sky-100 text-sky-700",         label: "In Progress" },
  failed:      { cls: "bg-rose-100 text-rose-700",       label: "Failed" },
  transferred: { cls: "bg-violet-100 text-violet-700",   label: "Transferred" },
  incomplete:  { cls: "bg-amber-100 text-amber-700",     label: "Incomplete" },
};
const EVAL_BADGE = {
  positive: { cls: "bg-emerald-100 text-emerald-700", label: "Successful" },
  neutral:  { cls: "bg-amber-100 text-amber-700",     label: "Needs Review" },
  negative: { cls: "bg-rose-100 text-rose-700",       label: "Poor" },
};

const fmtDT = (iso) => iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";
const fmtDur = (s) => {
  const v = Number(s);
  if (!v || v <= 0) return "—";
  const m = Math.floor(v / 60), sec = Math.round(v % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
};

function StatusBadge({ status }) {
  const b = STATUS_BADGE[status] || { cls: "bg-slate-100 text-slate-600", label: status || "—" };
  return <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${b.cls}`}>{b.label}</span>;
}
function EvalBadge({ sentiment }) {
  const b = EVAL_BADGE[sentiment];
  if (!b) return <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Not Evaluated</span>;
  return <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${b.cls}`}>{b.label}</span>;
}

export default function AiConversations() {
  const navigate = useNavigate();
  const { businessId } = useBusiness();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  // filters
  const [search, setSearch] = useState("");
  const [agentFilter, setAgentFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [channelFilter, setChannelFilter] = useState("all");
  const [evalFilter, setEvalFilter] = useState("all");
  const [datePreset, setDatePreset] = useState("all");
  // detail
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);        // { conversation, messages }
  const [detailLoading, setDetailLoading] = useState(false);
  const [creatingLead, setCreatingLead] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ page: String(page), page_size: "25" });
      if (businessId) qs.set("business_id", businessId);
      if (search) qs.set("search", search);
      if (agentFilter !== "all") qs.set("agent_id", agentFilter);
      if (statusFilter !== "all") qs.set("status", statusFilter);
      if (channelFilter !== "all") qs.set("channel", channelFilter);
      if (evalFilter !== "all") qs.set("sentiment", evalFilter);
      if (datePreset !== "all") {
        const [s, e] = computeDatePreset(datePreset);
        if (s) qs.set("start_date", s);
        if (e) qs.set("end_date", e);
      }
      const r = await fetch(`${BACKEND_URL}/api/ai-conversations?${qs}`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setItems(d.items || []);
        setTotal(d.total || 0);
        setPages(d.pages || 1);
      }
    } finally { setLoading(false); }
  }, [businessId, page, search, agentFilter, statusFilter, channelFilter, evalFilter, datePreset]);

  useEffect(() => { const t = setTimeout(fetchList, 250); return () => clearTimeout(t); }, [fetchList]);
  useEffect(() => { setPage(1); }, [search, agentFilter, statusFilter, channelFilter, evalFilter, datePreset, businessId]);

  // Agent options derived from the loaded rows (id → latest name seen)
  const agentOptions = useMemo(() => {
    const m = new Map();
    for (const c of items) if (c.agent_id) m.set(c.agent_id, c.agent_name || c.agent_id);
    return [...m.entries()];
  }, [items]);

  const openDetail = async (id) => {
    setOpenId(id);
    setDetail(null);
    setDetailLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/ai-conversations/${id}`, { credentials: "include" });
      if (r.ok) setDetail(await r.json());
      else toast.error("Failed to load conversation");
    } finally { setDetailLoading(false); }
  };

  const handleCreateLead = async () => {
    if (!openId) return;
    setCreatingLead(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/ai-conversations/${openId}/create-lead`, { method: "POST", credentials: "include" });
      const d = await r.json().catch(() => ({}));
      if (r.ok) {
        toast.success(d.existing ? "Linked to existing lead" : "Lead created");
        openDetail(openId);
        fetchList();
      } else toast.error(d.detail || "Failed to create lead");
    } finally { setCreatingLead(false); }
  };

  const conv = detail?.conversation;

  return (
    <div className="space-y-5" data-testid="ai-conversations-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <AudioLines className="w-6 h-6 text-indigo-600" /> AI Conversation
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            {total.toLocaleString("en-IN")} conversation{total === 1 ? "" : "s"} handled by your AI agents
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchList}><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-3">
        <div className="flex items-center gap-2 bg-slate-50 dark:bg-slate-800 rounded-lg px-3 py-1.5 flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search contact, phone, summary…"
            className="bg-transparent outline-none text-sm w-full" data-testid="aic-search" />
        </div>
        <Select value={agentFilter} onValueChange={setAgentFilter}>
          <SelectTrigger className="w-[170px]"><Bot className="w-3.5 h-3.5 mr-1.5 text-slate-400" /><SelectValue placeholder="Agent" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Agents</SelectItem>
            {agentOptions.map(([id, name]) => <SelectItem key={id} value={id}>{name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={channelFilter} onValueChange={setChannelFilter}>
          <SelectTrigger className="w-[130px]"><SelectValue placeholder="Channel" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Channels</SelectItem>
            <SelectItem value="voice">Voice</SelectItem>
            <SelectItem value="whatsapp">WhatsApp</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[140px]"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="done">Successful</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={evalFilter} onValueChange={setEvalFilter}>
          <SelectTrigger className="w-[150px]"><SelectValue placeholder="Evaluation" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Evaluations</SelectItem>
            <SelectItem value="positive">Successful</SelectItem>
            <SelectItem value="neutral">Needs Review</SelectItem>
            <SelectItem value="negative">Poor</SelectItem>
          </SelectContent>
        </Select>
        <Select value={datePreset} onValueChange={setDatePreset}>
          <SelectTrigger className="w-[140px]"><SelectValue placeholder="Date" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Dates</SelectItem>
            {DATE_PRESETS.map(p => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800 text-[11px] uppercase tracking-wide text-slate-500 sticky top-0">
              <tr>
                <th className="text-left px-4 py-3">Agent</th>
                <th className="text-left px-4 py-3">Conversation</th>
                <th className="text-left px-4 py-3">Date &amp; Time</th>
                <th className="text-right px-4 py-3">Duration</th>
                <th className="text-right px-4 py-3">Messages</th>
                <th className="text-left px-4 py-3">Contact</th>
                <th className="text-center px-4 py-3">Status</th>
                <th className="text-center px-4 py-3">Evaluation</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [0,1,2,3,4,5].map(i => (
                  <tr key={i} className="border-t border-slate-100 dark:border-slate-800">
                    <td colSpan={8} className="px-4 py-3"><div className="h-6 bg-slate-100 dark:bg-slate-800 rounded animate-pulse" /></td>
                  </tr>
                ))
              ) : items.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-16 text-center">
                  <AudioLines className="w-10 h-10 text-slate-200 mx-auto mb-3" />
                  <div className="text-slate-600 font-semibold">No conversations yet</div>
                  <div className="text-sm text-slate-400 mt-1">Connect ElevenLabs in Integrations and conversations will appear here.</div>
                </td></tr>
              ) : items.map(c => (
                <tr key={c.conversation_id} onClick={() => openDetail(c.conversation_id)}
                  className="border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/40 cursor-pointer"
                  data-testid={`aic-row-${c.conversation_id}`}>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold bg-indigo-50 text-indigo-700 px-2 py-1 rounded-lg whitespace-nowrap">
                      {c.channel === "whatsapp" ? <MessageCircle className="w-3.5 h-3.5" /> : <Bot className="w-3.5 h-3.5" />}
                      {c.agent_name || c.agent_id || "Agent"}
                    </span>
                  </td>
                  <td className="px-4 py-3 max-w-[260px]">
                    <div className="font-medium text-slate-800 dark:text-slate-200 truncate">
                      {c.intent || c.summary || c.customer_name || "Conversation"}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmtDT(c.started_at)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-600">{fmtDur(c.duration_seconds)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-600">{c.message_count ?? "—"}</td>
                  <td className="px-4 py-3">
                    <div className="text-slate-700 dark:text-slate-300 truncate max-w-[140px]">{c.customer_name || "—"}</div>
                    {c.customer_phone && <div className="text-[11px] text-slate-400">{c.customer_phone}</div>}
                  </td>
                  <td className="px-4 py-3 text-center"><StatusBadge status={c.status} /></td>
                  <td className="px-4 py-3 text-center"><EvalBadge sentiment={c.sentiment} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 dark:border-slate-800">
            <div className="text-xs text-slate-500">Page {page} of {pages} · {total.toLocaleString("en-IN")} total</div>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
              <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
        )}
      </div>

      {/* Detail drawer */}
      {openId && (
        <div className="fixed inset-0 z-50 flex justify-end" style={{ background: "rgba(15,23,42,.4)" }} onClick={() => setOpenId(null)}>
          <div className="w-full max-w-2xl bg-white dark:bg-slate-900 h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()} data-testid="aic-detail-drawer">
            <div className="sticky top-0 z-10 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-5 py-4 flex items-center justify-between">
              <h2 className="font-bold text-slate-900 dark:text-white truncate">
                {conv ? (conv.intent || conv.summary || conv.customer_name || "Conversation") : "Conversation"}
              </h2>
              <button onClick={() => setOpenId(null)} className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0"><X className="w-4 h-4" /></button>
            </div>
            {detailLoading || !conv ? (
              <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : (
              <div className="p-5 space-y-6">
                {/* Overview */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <Ov label="Agent" value={conv.agent_name || conv.agent_id} />
                  <Ov label="Channel" value={conv.channel === "whatsapp" ? "WhatsApp" : "Voice"} />
                  <Ov label="Date" value={fmtDT(conv.started_at)} />
                  <Ov label="Duration" value={fmtDur(conv.duration_seconds)} icon={Clock} />
                  <Ov label="Messages" value={conv.message_count ?? "—"} />
                  <Ov label="Language" value={conv.language || "auto"} />
                  <Ov label="Contact" value={conv.customer_name || "—"} />
                  <Ov label="Phone" value={conv.customer_phone || "—"} icon={Phone} />
                  <div>
                    <div className="text-[11px] text-slate-400 uppercase tracking-wide">Status / Evaluation</div>
                    <div className="mt-1 flex gap-1.5 flex-wrap"><StatusBadge status={conv.status} /><EvalBadge sentiment={conv.sentiment} /></div>
                  </div>
                </div>

                {/* CRM link */}
                <div className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 dark:border-slate-800 p-3">
                  {conv.lead_id ? (
                    <>
                      <span className="text-sm text-slate-600 dark:text-slate-300">Linked to CRM lead</span>
                      <Button size="sm" variant="outline" onClick={() => navigate(`/leads/${conv.lead_id}`)} data-testid="aic-open-lead">
                        <ExternalLink className="w-3.5 h-3.5 mr-1" /> Open Lead
                      </Button>
                    </>
                  ) : (
                    <>
                      <span className="text-sm text-slate-600 dark:text-slate-300">No CRM record linked{conv.customer_phone ? ` for ${conv.customer_phone}` : ""}</span>
                      <Button size="sm" onClick={handleCreateLead} disabled={creatingLead} className="bg-indigo-600 text-white" data-testid="aic-create-lead">
                        {creatingLead ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <UserPlus className="w-3.5 h-3.5 mr-1" />} Create Lead
                      </Button>
                    </>
                  )}
                </div>

                {/* Audio (voice calls) */}
                {conv.channel !== "whatsapp" && (
                  <div className="rounded-xl border border-slate-200 dark:border-slate-800 p-3">
                    <div className="text-[11px] text-slate-400 uppercase tracking-wide mb-2 flex items-center gap-1"><Play className="w-3 h-3" /> Call Recording</div>
                    <audio controls preload="none" className="w-full" src={`${BACKEND_URL}/api/ai-conversations/${conv.conversation_id}/audio`} />
                  </div>
                )}

                {/* Summary */}
                {conv.summary && (
                  <div className="rounded-xl bg-indigo-50/60 dark:bg-indigo-950/30 border border-indigo-100 dark:border-indigo-900 p-3">
                    <div className="text-[11px] text-indigo-500 uppercase tracking-wide mb-1">AI Summary</div>
                    <p className="text-sm text-slate-700 dark:text-slate-300">{conv.summary}</p>
                  </div>
                )}

                {/* Transcript */}
                <div>
                  <div className="text-[11px] text-slate-400 uppercase tracking-wide mb-3">Transcript ({detail.messages?.length || 0} messages)</div>
                  {(!detail.messages || detail.messages.length === 0) ? (
                    <div className="text-sm text-slate-400 italic">No transcript available for this conversation.</div>
                  ) : (
                    <div className="space-y-3">
                      {detail.messages.map(m => {
                        const isAgent = m.role === "agent" || m.role === "assistant" || m.role === "ai";
                        return (
                          <div key={m.message_id || m._id} className={`flex ${isAgent ? "justify-start" : "justify-end"}`}>
                            <div className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm ${
                              isAgent
                                ? "bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 rounded-tl-sm"
                                : "bg-indigo-600 text-white rounded-tr-sm"
                            }`}>
                              <div className={`text-[10px] font-bold mb-0.5 ${isAgent ? "text-slate-400" : "text-indigo-200"}`}>
                                {isAgent ? (conv.agent_name || "AI Agent") : (conv.customer_name || "Customer")}
                                {m.timestamp && <span className="font-normal ml-2">{new Date(m.timestamp).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</span>}
                              </div>
                              <div className="whitespace-pre-wrap break-words">{m.content}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Ov({ label, value }) {
  return (
    <div>
      <div className="text-[11px] text-slate-400 uppercase tracking-wide">{label}</div>
      <div className="text-[13.5px] font-medium text-slate-800 dark:text-slate-200 mt-0.5 truncate">{value}</div>
    </div>
  );
}
